import { Stack } from '@mantine/core';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { Button } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { IQuestion } from '../../api';
import {
	useAuthUser,
	useIntegrationList,
	useQuestionInfiniteList,
} from '../../api';
import type { FetchModelInfiniteListHook } from '../../api/factories/types';
import type { OpenEntityModalDataRelatedResource } from '../EntityModal/store';
import { entityModalStore } from '../EntityModal/store';
import { FeatureGuard } from '../FeatureGuard/FeatureGuard';
import { TableV2 } from '../TableV2';
import type { ExtendedDataTableColumn } from '../TableV2/types';
import { getDiscussionTeamIds } from './discussionTeams';
import { useColumns } from './EntityDiscussions.hooks';

export interface IEntityDiscussionsProps {
	entity: OpenEntityModalDataRelatedResource;
}

const COLUMN_VISIBILITY = {
	catalogType: EntityType.question,
	catalogServerType: EntityType.question,
} as const;

function EntityDiscussions({ entity }: IEntityDiscussionsProps) {
	const { user } = useAuthUser();
	const integrations = useIntegrationList({});
	const navigate = useNavigate();

	const columns = useColumns();

	const handleQuestionClick = useCallback(
		(id: string) => {
			navigate(`/questions/${id}`);
		},
		[navigate]
	);

	const onCellClick = useCallback(
		(cell: {
			column: ExtendedDataTableColumn<IQuestion>;
			record: { id: string };
		}) => {
			if (cell.column.navigate !== false) {
				handleQuestionClick(cell.record.id);
			}
		},
		[handleQuestionClick]
	);

	const handleButtonClick = useCallback(() => {
		entityModalStore.open({
			type: EntityType.question,
			relatedResource: entity,
			teamIds: getDiscussionTeamIds(entity, integrations.data?.results || []),
			metadata: {
				owners: [user.id],
			},
		});
	}, [entity, integrations.data?.results, user.id]);

	const defaultSearchParams = useMemo(
		() => ({
			entity_id: entity.id,
		}),
		[entity.id]
	);

	return (
		<FeatureGuard
			featureName="Questions"
			v1AllowedRoles={[
				UserRole.ADMIN,
				UserRole.EDITOR,
				UserRole.VIEWER,
				UserRole.GUEST,
			]}
			v2Permission="Questions.Read"
			isFeatureIncludedInPlan={true}
			isAlwaysIncludedFeature={true}
		>
			<Stack>
				<TableV2<IQuestion>
					pluralTypeString="questions"
					withCsvExport
					withInfiniteScroll
					usePaginationList={
						useQuestionInfiniteList as FetchModelInfiniteListHook<IQuestion>
					}
					onCellClick={onCellClick}
					columns={columns}
					columnVisibility={COLUMN_VISIBILITY}
					defaultRequiredSearchParams={defaultSearchParams}
					withAdditionalButtons={
						<Button
							variant="primary"
							leftIconName="messageCircleQuestion"
							onClick={handleButtonClick}
							data-testid="entity-discussions-create-question-button"
						>
							Create a question
						</Button>
					}
				/>
			</Stack>
		</FeatureGuard>
	);
}

const EntityDiscussionsObserver = observer(EntityDiscussions);
export default EntityDiscussionsObserver;
